// components/ScraperDashboard/ScheduledScrapesPanel.js
// Display and manage scheduled scrape jobs (cron)
import React, { useState, useEffect, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import {
  Clock, Plus, CheckCircle, AlertCircle, Trash2, Edit2,
  Calendar, ChevronDown, ChevronUp, RefreshCw, Power, PowerOff
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import useDepartments from '../../hooks/useDepartments';
import './ScheduledScrapesPanel.css';

// Cron presets for easy selection
const SCHEDULE_PRESETS = [
  { label: 'Daily 6 AM', cron: '0 6 * * *', description: 'Every day at 6:00 AM' },
  { label: 'Daily 10 PM', cron: '0 22 * * *', description: 'Every day at 10:00 PM' },
  { label: 'Weekly Mon', cron: '0 6 * * 1', description: 'Every Monday at 6:00 AM' },
  { label: 'Weekly Sun', cron: '0 6 * * 0', description: 'Every Sunday at 6:00 AM' },
  { label: 'Twice Daily', cron: '0 6,18 * * *', description: 'Every day at 6 AM and 6 PM' },
  { label: 'Every 6 Hours', cron: '0 */6 * * *', description: 'Every 6 hours' },
];

const ACQ_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'IT Goods', label: 'IT Goods' },
  { value: 'IT Services', label: 'IT Services' },
  { value: 'Non-IT Goods', label: 'Non-IT Goods' },
  { value: 'Non-IT Services', label: 'Non-IT Services' },
];

/**
 * ScheduledScrapesPanel - Display and manage scheduled scrape jobs
 */
function ScheduledScrapesPanel({ hideHeader = false, departmentCode = null }) {
  const { departments: dbDepts } = useDepartments();
  const deptOptions = [
    { code: '', name: 'All Departments' },
    ...dbDepts.map(d => ({ code: d.code, name: `${d.acronym} - ${d.name}` })),
  ];
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    schedule: '0 6 * * *',
    departmentCode: '',
    acquisitionType: 'IT Goods',
    lookbackDays: 30,
    enabled: true,
  });
  const [saving, setSaving] = useState(false);

  // Fetch scheduled scrapes
  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get('/api/scraper/schedules');
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error('[ScheduledScrapesPanel] Fetch error:', err);
      // Don't show error if endpoint doesn't exist yet
      if (!err.message?.includes('404')) {
        setError(err.message || 'Failed to load schedules');
      }
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await apiClient.post('/api/scraper/schedules', formData);
      setShowAddForm(false);
      setFormData({
        name: '',
        schedule: '0 6 * * *',
        departmentCode: '',
        acquisitionType: 'IT Goods',
        lookbackDays: 30,
        enabled: true,
      });
      fetchSchedules();
    } catch (err) {
      console.error('[ScheduledScrapesPanel] Save error:', err);
      setError(err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  // Toggle schedule enabled/disabled
  const toggleEnabled = async (id, enabled) => {
    try {
      await apiClient.put(`/api/scraper/schedules/${id}`, { enabled: !enabled });
      fetchSchedules();
    } catch (err) {
      console.error('[ScheduledScrapesPanel] Toggle error:', err);
    }
  };

  // Delete schedule
  const deleteSchedule = async (id) => {
    if (!(await confirmDialog('Delete this scheduled scrape?'))) return;

    try {
      await apiClient.del(`/api/scraper/schedules/${id}`);
      fetchSchedules();
    } catch (err) {
      console.error('[ScheduledScrapesPanel] Delete error:', err);
    }
  };

  const formatNextRun = (cronExpr) => {
    // Simple heuristic for next run display
    // In production, use a cron parser library
    if (cronExpr.includes('0 6 * * *')) return 'Tomorrow 6:00 AM';
    if (cronExpr.includes('0 22 * * *')) return 'Today 10:00 PM';
    if (cronExpr.includes('* * 1')) return 'Next Monday';
    if (cronExpr.includes('* * 0')) return 'Next Sunday';
    return 'Scheduled';
  };

  // Filter schedules to selected department
  const visibleSchedules = departmentCode
    ? schedules.filter(s => s.departmentCode === departmentCode || s.department_code === departmentCode)
    : schedules;

  return (
    <div className={`ssp-container ${expanded ? 'ssp-expanded' : 'ssp-collapsed'}`}>
      {/* Header */}
      {!hideHeader && (
        <div className="ssp-header" onClick={() => setExpanded(!expanded)}>
          <div className="ssp-header-left">
            <Clock size={16} />
            <span className="ssp-title">Scheduled Scrapes</span>
            {visibleSchedules.length > 0 && (
              <span className="ssp-count">{visibleSchedules.filter(s => s.enabled).length} active</span>
            )}
          </div>
          <div className="ssp-header-right">
            {expanded && (
              <button
                className="ssp-add-btn"
                onClick={(e) => { e.stopPropagation(); setShowAddForm(!showAddForm); }}
              >
                <Plus size={14} />
                Add
            </button>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>
      )}

      {(hideHeader || expanded) && (
        <div className="ssp-body">
          {/* Add Form */}
          {showAddForm && (
            <form className="ssp-form" onSubmit={handleSubmit}>
              <div className="ssp-form-header">
                <span>New Scheduled Scrape</span>
                <button type="button" className="ssp-form-close" onClick={() => setShowAddForm(false)}>×</button>
              </div>

              <div className="ssp-form-grid">
                <div className="ssp-form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Daily IT Goods"
                    required
                  />
                </div>

                <div className="ssp-form-group">
                  <label>Schedule</label>
                  <select
                    value={formData.schedule}
                    onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                  >
                    {SCHEDULE_PRESETS.map(p => (
                      <option key={p.cron} value={p.cron}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="ssp-form-group">
                  <label>Department</label>
                  <select
                    value={formData.departmentCode}
                    onChange={(e) => setFormData({ ...formData, departmentCode: e.target.value })}
                  >
                    {deptOptions.map(d => (
                      <option key={d.code} value={d.code}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="ssp-form-group">
                  <label>Acquisition Type</label>
                  <select
                    value={formData.acquisitionType}
                    onChange={(e) => setFormData({ ...formData, acquisitionType: e.target.value })}
                  >
                    {ACQ_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="ssp-form-group">
                  <label>Lookback (days)</label>
                  <input
                    type="number"
                    value={formData.lookbackDays}
                    onChange={(e) => setFormData({ ...formData, lookbackDays: parseInt(e.target.value) || 30 })}
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              <div className="ssp-form-actions">
                <button type="button" className="ssp-btn-cancel" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="ssp-btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Schedule'}
                </button>
              </div>
            </form>
          )}

          {/* Error */}
          {error && (
            <div className="ssp-error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="ssp-loading">
              <RefreshCw size={16} className="ssp-spin" />
              Loading schedules...
            </div>
          )}

          {/* Empty State */}
          {!loading && visibleSchedules.length === 0 && !showAddForm && (
            <div className="ssp-empty">
              <Calendar size={24} />
              <p>{departmentCode ? 'No schedules for this department' : 'No scheduled scrapes configured'}</p>
              <button className="ssp-btn-add" onClick={() => setShowAddForm(true)}>
                <Plus size={14} />
                Add Schedule
              </button>
            </div>
          )}

          {/* Schedule List */}
          {!loading && visibleSchedules.length > 0 && (
            <div className="ssp-list">
              {visibleSchedules.map(schedule => (
                <div key={schedule.id} className={`ssp-item ${schedule.enabled ? '' : 'ssp-disabled'}`}>
                  <div className="ssp-item-status">
                    {schedule.enabled ? (
                      <Power size={14} className="ssp-icon-on" />
                    ) : (
                      <PowerOff size={14} className="ssp-icon-off" />
                    )}
                  </div>

                  <div className="ssp-item-main">
                    <div className="ssp-item-name">{schedule.name}</div>
                    <div className="ssp-item-meta">
                      <span className="ssp-item-cron">{schedule.schedule}</span>
                      {schedule.departmentCode && (
                        <span className="ssp-item-dept">{schedule.departmentCode}</span>
                      )}
                      {schedule.acquisitionType && (
                        <span className="ssp-item-acq">{schedule.acquisitionType}</span>
                      )}
                    </div>
                    {schedule.lastRun && (
                      <div className="ssp-item-last">
                        Last: {schedule.lastRun.status === 'success' ? (
                          <><CheckCircle size={10} /> {schedule.lastRun.count} found</>
                        ) : (
                          <><AlertCircle size={10} /> Failed</>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="ssp-item-next">
                    <span className="ssp-next-label">Next</span>
                    <span className="ssp-next-time">{formatNextRun(schedule.schedule)}</span>
                  </div>

                  <div className="ssp-item-actions">
                    <button
                      className="ssp-action-btn"
                      onClick={() => toggleEnabled(schedule.id, schedule.enabled)}
                      title={schedule.enabled ? 'Disable' : 'Enable'}
                    >
                      {schedule.enabled ? <PowerOff size={14} /> : <Power size={14} />}
                    </button>
                    <button
                      className="ssp-action-btn ssp-action-delete"
                      onClick={() => deleteSchedule(schedule.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ScheduledScrapesPanel;
